import { AfterViewInit, Component, EventEmitter, inject, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { buildWaterfallOption } from '../../self-service-visualization/shared/self-service-recompute.utils';
import { formatNumber } from '../../self-service-visualization/shared/number-format.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperService, ChartWrapperInstance } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { DataErrorComponent } from '../../global-components/data-error/data-error.component';
import { buildConfigurableTooltipFormatter, buildConfigurableAxisLabel } from '../../utils/chart.utils';
import { CommonModule } from '@angular/common';
import { buildSecondaryXAxisOption, buildSecondaryYAxisOption, getDoubleAxisGridAdjustments } from '../../utils/axis.utils';

import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-waterfall-chart',
  standalone: true,
  imports: [DataErrorComponent, CommonModule, CapitalCasePipe],
  templateUrl: './waterfall-chart.component.html',
  styleUrl: './waterfall-chart.component.scss',
})
export class WaterfallChartComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() data!: any;
  /** Emitted on bar click when `data.crossFilter` is on (DVM builder tiles) —
   *  mirrors the unified bar chart's event: the column this chart publishes
   *  (data.filterKey, may be '' — the host resolves its own key) + the FULL
   *  multi-select selection in `values` (empty = clear the filter). */
  @Output() crossFilterChange = new EventEmitter<{ column: string; value: string | null; values: string[] }>();
  protected myChart!: echarts.ECharts;
  /** Clicked step labels (multi-select toggle) for crossFilter/highlightOnClick. */
  private clickSelection = new Set<string>();
  public hasError = false;
  public errorMessage = '';
  protected allCategories: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastChartData?: { category: string[]; series: any[] };
  private viewReady = false;
  private resizeObserver?: ResizeObserver;
  private pendingRenderHandle = 0;

  ngAfterViewInit(): void {
    this.viewReady = true;
    const initialFilters = this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => [],
      getCategories: () => this.allCategories,
      // Custom highlight: waterfall uses 0.3 opacity (not 0)
      onHighlightCategories: (cats) => this.applyCategoryHighlight(cats),
      onResetCategoryHighlight: () => this.resetCategoryHighlight(),
    });

    this.scheduleRender(initialFilters ?? undefined);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastChartData || !this.myChart) return;
      const chartDom = document.getElementById(this.data.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastChartData);
    });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady || !changes['data'] || changes['data'].firstChange) return;
    this.wrapper.updateData(this.data);
    this.scheduleRender();
  }
  private scheduleRender(filters?: any): void {
    if (this.pendingRenderHandle) cancelAnimationFrame(this.pendingRenderHandle);
    this.pendingRenderHandle = requestAnimationFrame(() => {
      this.pendingRenderHandle = 0;
      this.loadAndRenderChart(filters);
    });
  }
  protected loadAndRenderChart(filters?: any): void {
    // The error block replaces the chart container (*ngIf="!hasError"), so the
    // lookup below would find nothing and bail before clearing the flag. Clear
    // it first, then render next frame against the restored element.
    if (this.hasError) {
      this.hasError = false;
      this.disposeChartInstance();
      this.scheduleRender(filters);
      return;
    }

    const chartDom = document.getElementById(this.data.widgetId);
    if (!chartDom) return;

    // The *ngIf creates a new element each time it re-enters, so an instance
    // held over from a previous container points at a detached node.
    if (this.myChart && this.myChart.getDom() !== chartDom) this.disposeChartInstance();

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
      this.observeChartDom(chartDom);
    }

    // Pre-built option path: a host that already computed the option (the DVM
    // Layout Builder). Prefer rebuilding from the raw `waterfallBars` + `format`
    // so the number formatters (FUNCTIONS, dropped by JSON.stringify on publish)
    // are restored - the serialized `data.option` would render without them.
    // Falls back to the serialized `data.option` when no bars are present.
    if (Array.isArray(this.data?.waterfallBars) || this.data?.option) {
      const option = Array.isArray(this.data?.waterfallBars)
        ? buildWaterfallOption(this.data.waterfallBars, this.data.format)
        : this.applyFormatToSerializedOption(this.data.option, this.data.format);
      this.normalizePublishedWaterfallOption(option);
      this.hasError = false;
      this.allCategories = this.getXAxisCategories(option);
      applyRoleVisibilityToOption(option, this.data);
      this.myChart.hideLoading();
      this.myChart.setOption(option, true);
      this.myChart.resize();
      this.addResizeListener();
      this.bindInteractionClickHandler();
      this.wrapper.afterRender();
      return;
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters)?.subscribe({
      next: (res: any) => {
        const chartData = this.extractChartData(res);
        const isEmpty = !chartData || (Array.isArray(chartData) && chartData.length === 0);

        if (isEmpty) {
          this.hasError = true;
          this.errorMessage = 'No data found for the selected filters.';
          this.myChart?.clear();
        } else {
          this.hasError = false;
          this.renderChart(chartData);
          this.addResizeListener();
        }

        this.myChart?.hideLoading();
      },
      error: () => {
        this.hasError = true;
        this.errorMessage = 'No data available for the chart.';
        this.myChart?.clear();
        this.myChart?.hideLoading();
      },
    });

  }

  /**
   * Published DVM layouts can arrive from the instant local cache or from an
   * older API record containing only a serialized ECharts option. Normalize
   * both shapes here so the running-balance spacer can never inherit a theme
   * colour and visually turn the waterfall into ordinary bars from zero.
   */
  private normalizePublishedWaterfallOption(option: any): void {
    if (!option || !Array.isArray(option.series)) return;

    const transparentStyle = {
      color: 'transparent',
      borderColor: 'transparent',
      borderWidth: 0,
    };

    option.series = option.series.map((series: any) => {
      const name = String(series?.name || '').trim().toLowerCase();
      const isSpacer = name === 'base' || name === 'placeholder';
      const stackedSeries = series?.stack ? { stackStrategy: 'all' } : {};
      if (!isSpacer) return { ...series, ...stackedSeries };

      const data = Array.isArray(series.data)
        ? series.data.map((item: any) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
              return { ...item, itemStyle: { ...(item.itemStyle || {}), ...transparentStyle } };
            }
            return { value: item, itemStyle: { ...transparentStyle } };
          })
        : series.data;

      return {
        ...series,
        ...stackedSeries,
        silent: true,
        itemStyle: { ...(series.itemStyle || {}), ...transparentStyle },
        emphasis: {
          ...(series.emphasis || {}),
          disabled: true,
          itemStyle: { ...(series.emphasis?.itemStyle || {}), ...transparentStyle },
        },
        data,
      };
    });
  }

  /**
   * A serialized `option` (no `waterfallBars` alongside it — an older published
   * record from before bars-persistence, or a widget that otherwise lost its raw
   * bars) has its axis/tooltip/label formatter FUNCTIONS stripped by
   * JSON.stringify, so the Number Format popup selection has no effect once
   * rendered from this path. Re-derive those formatters from `format` here so
   * changing the dropdown still takes effect even without raw bars to rebuild
   * the option from scratch.
   */
  private applyFormatToSerializedOption(option: any, format?: Record<string, unknown>): any {
    if (!option || typeof option !== 'object' || !Array.isArray(option.series)) return option;

    const numberFormat = (format?.['numberFormat'] as string) || 'NONE';
    const showValues = format?.['showValues'] !== false;
    // Same affixes as buildWaterfallOption: Y wraps amounts, X wraps step labels.
    const xAxisPrefix = String(format?.['xAxisPrefix'] ?? '');
    const xAxisSuffix = String(format?.['xAxisSuffix'] ?? '');
    const yAxisPrefix = String(format?.['yAxisPrefix'] ?? '');
    const yAxisSuffix = String(format?.['yAxisSuffix'] ?? '');
    const fmt = (value: number) => `${yAxisPrefix}${formatNumber(value, numberFormat)}${yAxisSuffix}`;
    const isSpacerName = (name: unknown) => {
      const n = String(name ?? '').trim().toLowerCase();
      return n === 'base' || n === 'placeholder';
    };
    const valueOf = (v: unknown): number => (v && typeof v === 'object' && !Array.isArray(v) ? Number((v as { value?: unknown }).value) : Number(v));

    const patchAxis = (axis: unknown): unknown => ({
      ...((axis as Record<string, unknown>) || {}),
      axisLabel: { ...(((axis as Record<string, unknown>)?.['axisLabel'] as Record<string, unknown>) || {}), formatter: (value: number) => fmt(value) },
    });
    const yAxis = Array.isArray(option.yAxis) ? option.yAxis.map(patchAxis) : patchAxis(option.yAxis);
    const patchCategoryAxis = (axis: unknown): unknown => ({
      ...((axis as Record<string, unknown>) || {}),
      axisLabel: { ...(((axis as Record<string, unknown>)?.['axisLabel'] as Record<string, unknown>) || {}), formatter: (value: string) => `${xAxisPrefix}${value}${xAxisSuffix}` },
    });
    const xAxis = option.xAxis === undefined
      ? undefined
      : Array.isArray(option.xAxis) ? option.xAxis.map(patchCategoryAxis) : patchCategoryAxis(option.xAxis);

    const series = option.series.map((s: any) => {
      if (isSpacerName(s?.name)) return s;
      return {
        ...s,
        label: {
          ...(s?.label || {}),
          show: showValues,
          formatter: (params: { value: unknown }) => {
            const raw = valueOf(params.value);
            const sign = raw > 0 ? '+' : '';
            return `${sign}${fmt(Math.abs(raw))}`;
          },
        },
      };
    });

    return {
      ...option,
      ...(xAxis !== undefined ? { xAxis } : {}),
      yAxis,
      series,
      tooltip: {
        ...(option.tooltip || {}),
        formatter: (params: unknown) => {
          const list = Array.isArray(params) ? params : [params];
          const real = (list as any[]).find((p) => !isSpacerName(p?.seriesName) && p?.value !== '-' && p?.value != null);
          if (!real) return '';
          const raw = valueOf(real.value);
          const sign = raw > 0 ? '+' : '';
          return `${real.name}<br/>${real.seriesName}: ${sign}${fmt(Math.abs(raw))}`;
        },
      },
    };
  }

  private disposeChartInstance(): void {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;
    this.myChart?.dispose();
    this.myChart = undefined as unknown as echarts.ECharts;
  }

  private observeChartDom(chartDom: HTMLElement): void {
    if (typeof ResizeObserver === 'undefined') return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
    this.resizeObserver.observe(chartDom);
  }

  private getXAxisCategories(option: any): string[] {
    const xAxis = option?.xAxis;
    const data = Array.isArray(xAxis) ? xAxis[0]?.data : xAxis?.data;
    return Array.isArray(data) ? data : [];
  }

  protected extractChartData(res: any): { category: string[]; series: any[] } | undefined {
    const dataKey = this.data?.dataKey;

    // New platform format: res[dataKey] has { category, series }
    if (dataKey && res?.[dataKey]?.category && res?.[dataKey]?.series) {
      return res[dataKey];
    }

    // Old format: res.data has { category, series }
    if (res?.data?.category && res?.data?.series) {
      return res.data;
    }

    // Direct format: res itself has { category, series }
    if (res?.category && res?.series) {
      return res;
    }

    // Flat array format: transform using categoryKey/valueKey
    const flatRows = Array.isArray(res) ? res
      : Array.isArray(res?.data) ? res.data
      : null;
    if (flatRows?.length && this.data?.categoryKey && this.data?.valueKey) {
      return this.flatRowsToWaterfall(flatRows, this.data.categoryKey, this.data.valueKey);
    }

    return undefined;
  }

  private flatRowsToWaterfall(rows: any[], categoryKey: string, valueKey: string): { category: string[]; series: any[] } {
    const totals = new Map<string, number>();
    for (const row of rows) {
      const cat = String(row[categoryKey] ?? '');
      totals.set(cat, (totals.get(cat) ?? 0) + (Number(row[valueKey]) || 0));
    }
    const category = Array.from(totals.keys());
    const values = category.map(c => totals.get(c) ?? 0);
    return {
      category,
      series: [
        {
          name: 'Placeholder',
          type: 'bar',
          stack: 'total',
          itemStyle: { borderColor: 'transparent', color: 'transparent' },
          emphasis: { itemStyle: { borderColor: 'transparent', color: 'transparent' } },
          data: values.map(() => 0),
        },
        {
          name: this.data?.valueKey || 'Value',
          type: 'bar',
          stack: 'total',
          label: { show: true, position: 'top' },
          data: values,
        },
      ],
    };
  }

  protected renderChart(chartData?: { category: string[]; series: any[] }): void {
    if (chartData) this.lastChartData = chartData;
    const theme = this.themeService.resolve(this.data?.themeOverrides);
    this.allCategories = chartData?.category || [];

    const categories = this.allCategories;
    const gridAdj = getDoubleAxisGridAdjustments(this.data);

    const primaryXAxis: any = {
      type: 'category',
      triggerEvent: true,
      // Keep the axis line + category labels at the BOTTOM of the plot instead
      // of ECharts' default y=0 position — a bridge that dips negative would
      // otherwise have the axis slicing through the middle of the chart.
      axisLine: { onZero: false },
      ...(buildConfigurableAxisLabel(this.data?.chartConfig?.xAxis)
        ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.xAxis) }
        : {}),
    };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    const primaryYAxis: any = {
      type: 'value',
      ...(buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis)
        ? { axisLabel: buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) }
        : {}),
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const option = {
      title: {},
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          const configurableFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
          if (configurableFormatter) return configurableFormatter;

          return (params: any) => {
            let tar;
            if (params[1] && params[1].value !== '-') {
              tar = params[1];
            } else {
              tar = params[2];
            }
            return `${tar.name}<br/>${tar.seriesName} : ${tar.value}`;
          };
        })(),
      },
      legend: {
        data: ['Expenses', 'Income'],
      },
      grid: {
        left: '3%',
        right: `${4 + gridAdj.extraRight}%`,
        bottom: `${3 + gridAdj.extraBottom}%`,
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: (chartData?.series || []).map((s: any) => ({
        ...s,
        label: {
          ...(s?.label || {}),
          color: theme.chart?.textColor || '#333',
          textBorderColor: 'transparent',
          textBorderWidth: 0,
          textShadowBlur: 0,
          textShadowColor: 'transparent',
        },
      })),
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    setTimeout(() => this.myChart.hideLoading(), 1000);

    this.bindInteractionClickHandler();
    // Wrapper re-applies visual state and wires click events
    this.wrapper.afterRender();
  }

  /** Click-to-interact (Interaction accordion, DVM builder tiles): cross-filter
   *  publish and/or self highlight. Bound with a STABLE handler ref so re-binding
   *  on every render never stacks duplicates — `off(handler)` removes only OURS,
   *  never the wrapper's own listeners. Flags are read at click time, so toggling
   *  them in the Format popup takes effect without a re-bind. */
  private bindInteractionClickHandler(): void {
    if (!this.myChart) return;
    this.myChart.off('click', this.onInteractionClick);
    this.myChart.on('click', this.onInteractionClick);
  }

  private onInteractionClick = (params: unknown): void => {
    const wantsCrossFilter = this.data?.crossFilter === true;
    const wantsHighlight = this.data?.highlightOnClick === true;
    if (!wantsCrossFilter && !wantsHighlight) return;
    const point = params as { componentType?: string; seriesName?: string; name?: unknown };
    // Only the colored Delta bars are clickable targets — the transparent Base
    // spacer is silent, and the legend marker series carry no data.
    if (point?.componentType !== 'series' || point?.seriesName !== 'Delta') return;
    const label = String(point?.name ?? '');
    if (!label) return;
    // Multi-select toggle (same semantics as the unified bar chart).
    if (this.clickSelection.has(label)) this.clickSelection.delete(label);
    else this.clickSelection.add(label);
    const values = Array.from(this.clickSelection);
    if (wantsHighlight) {
      if (values.length) this.applyCategoryHighlight(values);
      else this.resetCategoryHighlight();
    }
    if (wantsCrossFilter) {
      this.crossFilterChange.emit({
        column: (this.data?.filterKey as string) || '',
        value: values.length ? label : null,
        values,
      });
    }
  };

  // Custom highlight: waterfall uses opacity 0.3 for dimmed (not 0 like default wrapper)
  protected applyCategoryHighlight(categories?: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const cats = categories || this.wrapper.selectedCategories;
    const categoryIndices = cats
      .map(cat => this.allCategories.indexOf(cat))
      .filter(i => i !== -1);
    if (categoryIndices.length === 0) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;
      return {
        ...s,
        data: s.data.map((item: any, idx: number) => {
          const isMatch = categoryIndices.includes(idx);
          if (typeof item === 'object' && item !== null) {
            return { ...item, itemStyle: { ...(item.itemStyle || {}), opacity: isMatch ? 1 : 0.3 } };
          }
          return { value: item, itemStyle: { opacity: isMatch ? 1 : 0.3 } };
        }),
      };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  protected resetCategoryHighlight(): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;
      return {
        ...s,
        data: s.data.map((item: any) => {
          if (typeof item === 'object' && item !== null) {
            return { ...item, itemStyle: { ...(item.itemStyle || {}), opacity: 1 } };
          }
          return { value: item, itemStyle: { opacity: 1 } };
        }),
      };
    });

    this.myChart.setOption({ series: updatedSeries });
  }

  protected addResizeListener(): void {
    window.addEventListener('resize', this.handleResize);
  }

  protected handleResize = (): void => {
    this.myChart?.resize();
  };

  ngOnDestroy(): void {
    if (this.pendingRenderHandle) cancelAnimationFrame(this.pendingRenderHandle);
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.handleResize);
    this.myChart?.dispose();
  }
}



